'use client';

import { parseSegments } from './_utils/segments';
import { setLeaderRedirectUrlFromQuery } from '@utils/leaderRedirect';
import { AuthProvider } from 'app/_modules/auth/providers/AuthProvider/AuthProvider';
import { CommonAuthProvider } from 'app/_modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { FeatureFlagsProvider } from 'app/_modules/feature-flags/provider/FeatureFlagsProvider';
import { type DayjsWindow } from 'app/_modules/finops-center/shared/lib/convertWindowToDayjsUnits';
import { ServerFlagsProvider, type ServerFlags } from 'app/_providers/ServerFlagsContext';

import { StyleProvider, legacyLogicalPropertiesTransformer, legacyNotSelectorLinter } from '@ant-design/cssinjs';
import { App as AntdApp, ConfigProvider, ThemeConfig } from 'antd';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import { usePathname, useSearchParams, useSelectedLayoutSegments } from 'next/navigation';
import React from 'react';

// Allow dayjs to be used in the client
// with all necessary plugins enabled
dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(quarterOfYear);

// Augment dayjs types to support our consumption window units
// This solves the TypeScript overload resolution issue when using union types (TS#14107)
declare module 'dayjs' {
  interface Dayjs {
    add(value: number, unit: DayjsWindow): Dayjs;
    subtract(value: number, unit: DayjsWindow): Dayjs;
    startOf(unit: DayjsWindow): Dayjs;
    endOf(unit: DayjsWindow): Dayjs;
  }
}

// It's a recreated theme from the legacy Ant Design v4 theme
const legacyAntdTheme: ThemeConfig = {
  token: {
    borderRadius: 2,
    colorPrimary: '#1890ff',
    wireframe: true,
    fontFamily: "'Open Sans', sans-serif",
  },
  components: {
    Menu: {
      itemBorderRadius: 0,
      subMenuItemBorderRadius: 0,
      itemHoverColor: '#1890ff',
      itemSelectedColor: '#1890ff',
      itemSelectedBg: '#e6f7ff',
      activeBarWidth: 3,
      itemMarginInline: 0,
      itemHoverBg: 'transparent',
      fontSize: 12,
    },
    Alert: {
      defaultPadding: '8px 15px',
    },
    Table: {
      headerSortActiveBg: '#f5f5f5',
      headerFilterHoverBg: 'rgba(0,0,0,.04)',
      headerSortHoverBg: '#f5f5f5',
    },
  },
  hashed: false,
};

interface ClientRootProps {
  children: React.ReactNode;
  serverFlags: ServerFlags;
}

export const ClientRoot: React.FC<ClientRootProps> = ({ children, serverFlags }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const segments = parseSegments(useSelectedLayoutSegments() || []);
  const organizationId = segments.getIdByName('organizations');

  const from = searchParams?.get('from');

  React.useEffect(() => {
    if (from) {
      setLeaderRedirectUrlFromQuery();
    }
  }, [organizationId, from, pathname]);

  return (
    <ServerFlagsProvider flags={serverFlags}>
      <StyleProvider
        hashPriority='high'
        layer={false}
        linters={[legacyNotSelectorLinter]}
        transformers={[legacyLogicalPropertiesTransformer]}
      >
        <ConfigProvider theme={legacyAntdTheme}>
          <AntdApp>
            <AuthProvider>
              <CommonAuthProvider>
                <FeatureFlagsProvider>
                  {children}
                </FeatureFlagsProvider>
              </CommonAuthProvider>
            </AuthProvider>
          </AntdApp>
        </ConfigProvider>
      </StyleProvider>
    </ServerFlagsProvider>
  );
};
